
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DeleteInventoryPeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId: string;
  periodName: string;
}

export const DeleteInventoryPeriodDialog = ({
  open,
  onOpenChange,
  periodId,
  periodName
}: DeleteInventoryPeriodDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!periodId) return;
    
    setIsDeleting(true);
    try {
      // First delete related movements
      const { error: movementsError } = await supabase
        .from("inventory_movements")
        .delete()
        .eq("period_id", periodId);
      
      if (movementsError) throw movementsError;
      
      // Then delete related products
      const { error: productsError } = await supabase
        .from("inventory_products")
        .delete()
        .eq("period_id", periodId);
      
      if (productsError) throw productsError;
      
      // Finally delete the period
      const { error } = await supabase
        .from("inventory_periods")
        .delete()
        .eq("id", periodId);

      if (error) throw error;

      toast({
        title: "Période supprimée",
        description: "La période d'inventaire a été supprimée avec succès",
      });

      queryClient.invalidateQueries({ queryKey: ["inventory-periods"] });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error deleting inventory period:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la période d'inventaire: " + error.message,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
          <AlertDialogDescription>
            Êtes-vous sûr de vouloir supprimer la période d'inventaire "{periodName}" ?
            Cette action supprimera également tous les produits et mouvements associés à cette période.
            Cette action est irréversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Suppression..." : "Supprimer"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
